/**
 * @fileoverview Autocomplete Setup Module.
 * This module configures the Algolia Autocomplete functionality for various indexes like news, actions, resources, and global networks.
 * It includes custom rendering, event handling, and UI modifications for the autocomplete feature.
 */

import algoliasearch from 'algoliasearch/lite';
import { autocomplete } from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic';
import { AutocompleteItemTemplate } from './widgets/AutocompleteItemTemplate';
import {
    getIndicesWithKeysAsObject,
    roundRobinBestResultsFromAllIndices
} from "../Components/SearchPage/SearchPageUtils";

const searchClient = algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_READ_KEY);

const urlParams = new URLSearchParams(window.location.search);
const initialQuery = urlParams.get('wbcsd_action[query]');

/**
 * @constant
 * @type {object}
 * @description Class names used for styling various parts of the autocomplete component.
 */
const classNames = {
    detachedCancelButton: 'pxl-headerDetachedCancelButton',
    detachedFormContainer: 'pxl-headerDetachedFormContainer',
    detachedContainer: 'pxl-headerDetachedContainer',
    detachedOverlay: 'pxl-headerDetachedOverlay',
    detachedSearchButton: 'pxl-headerDetachedSearchButton',
    detachedSearchButtonIcon: 'pxl-headerDetachedSearchButtonIcon',
    detachedSearchButtonPlaceholder: 'pxl-headerDetachedSearchButtonPlaceholder',
    detachedSearchButtonQuery: 'pxl-headerDetachedSearchButtonQuery',
    form: 'pxl-headerForm',
    input: 'pxl-headerInput',
    inputWrapper: 'pxl-headerInputWrapper',
    inputWrapperPrefix: 'pxl-headerInputWrapperPrefix',
    inputWrapperSuffix: 'pxl-headerInputWrapperSuffix',
    item: 'pxl-headerItem',
    label: 'pxl-headerLabel',
    list: 'pxl-headerList',
    loadingIndicator: 'pxl-headerLoadingIndicator',
    panel: 'pxl-headerPanel',
    panelLayout: 'pxl-headerPanelLayout',
    clearButton: 'pxl-headerClearButton',
    root: 'pxl-headerRoot',
    source: 'pxl-headerSource',
    sourceFooter: 'pxl-headerSourceFooter',
    sourceHeader: 'pxl-headerSourceHeader',
    sourceNoResults: 'pxl-headerSourceNoResults',
    submitButton: 'pxl-headerSubmitButton'
};

/**
 * Initializes the header autocomplete functionality.
 * Configures multiple Algolia autocomplete sources for different content types and sets up event handling.
 */
window.headerAutocomplete = autocomplete({
    container: '#autocomplete',
    placeholder: 'search',
    openOnFocus: false,
    detachedMediaQuery: '(max-width: 1170px)',
    classNames,
    initialState: {
        query: initialQuery || '',
    },
    getSources() {
        return [
            {
                sourceId: 'combined',
                async getItems(query) {
                    const indices = getIndicesWithKeysAsObject();
                    const hitsPerPage = Object.keys(indices).length;
                    return await roundRobinBestResultsFromAllIndices(searchClient, query.query, hitsPerPage);
                },
                templates: {
                    item: AutocompleteItemTemplate,
                    footer() {
                        return `
                        `;
                    }
                },
            },
        ];
    },
    onSubmit({ state }) {
        executeSearch(state.query);
    }
});

const executeSearch = (query) => {
    window.location.href = `/search-page${query ? `?query=${encodeURIComponent(query)}` : ''}`
}

// mobile search hotfix WD-690
document.addEventListener('keyup', (event) => {
    if (event.key === 'Enter' && event.target.classList.contains('aa-Input')) {
        executeSearch(event.target.value);
    }
    const showAllResults = document.querySelector('.show-all-results-js');
    if (showAllResults) {
        showAllResults.href = `/search-page?query=${encodeURIComponent(event.target.value)}`;
    }
});

/**
 * @constant
 * @type {HTMLElement}
 * @description The close button element for the autocomplete.
 */
const closeButton = document.getElementById('closeAutocomplete');
closeButton.style.display = 'none';

/**
 * @constant
 * @type {HTMLElement}
 * @description The input element for the autocomplete.
 */
const inputElement = document.querySelector('.pxl-headerInput');
if (inputElement) {
    setupInputEvents(inputElement);
}

/**
 * Observes DOM mutations to apply custom behaviors to dynamically added elements by autocomplete.
 */
const observer = new MutationObserver((mutationsList) => {
    for (let mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (let node of mutation.addedNodes) {
                if (node instanceof Element) {
                    const mobileInput = node.querySelector('.pxl-headerDetachedFormContainer input.pxl-headerInput');
                    const targetInput = node.querySelector('.pxl-headerInput');
                    const aaPanel = node.classList.contains('pxl-headerPanel') ? node : false;
                    addButtonToDiv();
                    if (targetInput) {
                        setupInputEvents(targetInput);
                    }

                    if (mobileInput) {
                        addMagnifierIcon(mobileInput);
                        addCrossIconWithCancelButtonBehavior(mobileInput);
                    }

                    if (aaPanel) {
                        positionPanel(aaPanel);
                    }
                }
            }
        }
    }
});

/**
 * Sets up focus and blur event listeners on the given input element.
 * @param {HTMLElement} inputElement - The input element to attach events to.
 */
function setupInputEvents(inputElement) {
    inputElement.addEventListener('focus', function () {
        closeButton.style.display = 'block';
    });

    inputElement.addEventListener('blur', function () {
        closeButton.style.display = 'none';
    });
}

/**
 * Adds a custom magnifier icon to the specified input element.
 * @param {HTMLElement} inputElement - The input element to add the magnifier icon to.
 */
function addMagnifierIcon(inputElement) {
    if (inputElement.parentNode.querySelector('.custom-magnifier-icon')) {
        return;
    }

    const magnifierIcon = document.createElement('img');
    magnifierIcon.src = '/wp-content/themes/wbcsd/src/img/magnifier_searcher.svg';
    magnifierIcon.classList.add('custom-magnifier-icon');

    magnifierIcon.style.position = 'absolute';
    magnifierIcon.style.left = '10px';
    magnifierIcon.style.top = '50%';
    magnifierIcon.style.transform = 'translateY(-50%)';

    inputElement.parentNode.appendChild(magnifierIcon);
}

/**
 * Adds a custom cross icon with cancel button behavior to the specified input element.
 * @param {HTMLElement} inputElement - The input element to add the cross icon to.
 */
function addCrossIconWithCancelButtonBehavior(inputElement) {
    if (inputElement.parentNode.querySelector('.custom-cross-icon')) {
        return;
    }

    const crossIcon = document.createElement('img');
    crossIcon.src = '/wp-content/themes/wbcsd/src/img/cross_searcher.svg';
    crossIcon.classList.add('custom-cross-icon');

    crossIcon.style.position = 'absolute';
    crossIcon.style.right = '10px';
    crossIcon.style.top = '50%';
    crossIcon.style.transform = 'translateY(-50%)';
    crossIcon.style.cursor = 'pointer';

    crossIcon.addEventListener('click', function () {
        const cancelButton = document.querySelector('.pxl-headerDetachedCancelButton');
        if (cancelButton) {
            cancelButton.click();
        }
    });

    inputElement.parentNode.appendChild(crossIcon);
}

/**
 * Event listener to trigger the search on click of the custom magnifier icon.
 * It simulates a click on the detached search button, activating the search.
 */
document.querySelector('.custom-magnifier-icon').addEventListener('click', function () {
    const detachedSearchButton = document.querySelector('.pxl-headerDetachedSearchButton');
    if (detachedSearchButton) {
        // hotfix for the search button not working on mobile WD-691
        // TODO: Find a better solution for this
        setTimeout(() => {
            detachedSearchButton.click();
        }, 100);
    }
});

/**
 * Adjusts the position of the given panel element.
 * It modifies the panel's top position based on the current scroll position of the window.
 * @param {HTMLElement} panel - The panel element to reposition.
 */
function positionPanel(panel) {
    panel.style.top = (parseFloat(panel.style.top) - window.scrollY) + 'px';
}

/**
 * Adjusts the position of the autocomplete panel on window resize.
 */
function handleResize() {
    const panel = document.querySelector('.pxl-headerPanel');
    if (panel) {
        panel.classList.add('fixed');
        panel.style.top = (parseFloat(panel.style.top) - window.scrollY) + 'px';
    }
}
let resizeTimer;

/**
 * Event listener for window resize.
 * Debounces the resize event to prevent excessive handleResize calls.
 */
window.addEventListener('resize', () => {
    if (resizeTimer) {
        clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(() => {
        handleResize();
    }, 100);
});

/**
 * Event listener for document click.
 * Closes the autocomplete if the click is outside the autocomplete area.
 */
document.addEventListener('click', (event) => {
    const clickInsideAutocomplete = event.target.closest('.aa-Autocomplete');
    if (!clickInsideAutocomplete) {
        // window.headerAutocomplete.setIsOpen(false);
        window.newsAutocomplete?.setIsOpen(false);
        window.resourceAutocomplete?.setIsOpen(false);
    }
});


/**
 * Mutation observer to observe changes in the DOM.
 * It is used to apply custom behaviors to dynamically added elements by autocomplete.
 */
observer.observe(document.body, {
    childList: true,
    subtree: true
});
function addButtonToDiv() {
    const targetDiv = document.querySelector('.pxl-headerSourceFooter');
    let anchor = document.querySelector('.show-all-results-js');
    if (targetDiv && !anchor) {
        const inputValue = document.querySelector('.pxl-headerInput').value;
        console.log('inputValue: ', inputValue);

        anchor = document.createElement('a');
        anchor.classList.add('show-all-results-js');
        anchor.classList.add('flex');
        anchor.classList.add('gap-2');
        anchor.classList.add('items-center');
        anchor.href = `/search-page?query=${inputValue}`;

        const img = document.createElement('img');
        img.src = '/wp-content/themes/wbcsd/src/img/icon-chevron-right-black.svg';
        img.classList.add('h-[12px]');
        img.alt = '';

        anchor.appendChild(img);
        anchor.appendChild(document.createTextNode(' Click here or press enter for all results'));

        targetDiv.appendChild(anchor);
    } else {
        console.warn('Div with class "pxl-headerSourceFooter" not found.');
    }
}


const sourceFooterObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
            const targetDiv = document.querySelector('.pxl-headerSourceFooter');
            if (targetDiv) {
                addButtonToDiv();
                sourceFooterObserver.disconnect();
            }
        }
    });
});

sourceFooterObserver.observe(document.body, { childList: true, subtree: true });

addButtonToDiv();